/**
 * Approved, versioned Help Center corpus (UX-HELP-CENTER-001).
 *
 * This is the single source the Help Center searches and answers from. The
 * "ask" flow never generates text: it only retrieves and quotes sections from
 * this file, so it cannot invent product behavior. Anything not covered here
 * is reported as unknown.
 *
 * Content rules (enforced by helpTopics.test.js):
 *  - no secrets, credentials, hostnames, IPs, URLs or customer/private data
 *  - no description of hidden permissions or internal implementation detail
 *  - `audience: "admin"` topics are only shown to Admin/Owner users
 *  - bump HELP_CONTENT_VERSION (and a topic's `updated`) when product
 *    behavior changes, so stale help is detectable.
 */

export const HELP_CONTENT_VERSION = "2026-10-12.1";

export const HELP_TOPICS = [
  {
    id: "navigation",
    title: "ניווט במערכת",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["תפריט", "ניווט", "סרגל צד", "מסכים", "איפה", "מצא", "navigation", "menu", "sidebar"],
    summary:
      "התפריט בצד מרכז את כל המסכים. פריטים מסוימים מוצגים רק לפי תפקיד או הרשאה.",
    sections: [
      {
        heading: "מה מוצג בתפריט",
        text: "התפריט כולל את העבודה השוטפת (לוח בקרה, קריאות, פרויקטים, תחזוקה), ניהול לקוחות, מסכי סוכני ה-AI, ומסכי מערכת. אם פריט אינו מופיע אצלך, ייתכן שהוא דורש תפקיד או הרשאה אחרים.",
      },
      {
        heading: "כפתור המידע",
        text: "ליד כל פריט בתפריט יש כפתור ⓘ שמסביר מה המסך, מה הוא עושה ומתי להשתמש בו, וגם אם הוא לצפייה בלבד או מאפשר פעולות.",
      },
    ],
    related: ["system-map", "tickets"],
    source: "תפריט הניווט",
  },
  {
    id: "tickets",
    title: "קריאות שירות",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["קריאה", "קריאות", "טיקט", "פנייה", "תמיכה", "לקוח", "ticket", "tickets", "שיוך", "סטטוס"],
    summary:
      "קריאת שירות היא פנייה של לקוח שנרשמת, משויכת לטכנאי ומטופלת עד לסגירה.",
    sections: [
      {
        heading: "מהלך טיפול",
        text: "קריאה נקלטת (בדואר או ידנית), משויכת לטכנאי, מטופלת ומתועדת, ונסגרת. כל שינוי נרשם ביומן הפעולות.",
      },
      {
        heading: "סוכני AI בקריאה",
        text: "סוכן AI יכול לנתח קריאה ולהציע פתרון. הצעה של סוכן אינה מבוצעת מעצמה: שליחה ללקוח ופעולות משנות-מצב דורשות אישור אנושי.",
      },
    ],
    related: ["agents", "approvals"],
    source: "מסך קריאות שירות",
  },
  {
    id: "agents",
    title: "סוכני AI (עובדים דיגיטליים)",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["סוכן", "סוכנים", "עובד דיגיטלי", "ריצה", "ריצות", "agent", "agents", "AI", "בינה מלאכותית", "employee"],
    summary:
      "סוכן AI מנתח ומציע פעולות בתוך גבולות שהוגדרו לו. הוא אינו מחליט לבד מה מותר לו לבצע.",
    sections: [
      {
        heading: "מה סוכן עושה",
        text: "סוכן מקבל משימה (למשל קריאה), אוסף הקשר, ומציע ניתוח או פעולה. כל ריצה נשמרת ואפשר לעקוב אחריה במסך ריצות AI.",
      },
      {
        heading: "מה סוכן לא עושה",
        text: "סוכן אינו מעניק לעצמו הרשאות ואינו מאשר את עצמו. הרשאה לפעול נקבעת במערכת הבקרה ועל ידי אדם.",
      },
      {
        heading: "מסכים קשורים",
        text: "סוכני AI, ריצות AI, הרשאות AI ו-Command Center. חלק מהמסכים דורשים הרשאת צפייה או ניהול בסוכנים.",
      },
    ],
    related: ["approvals", "tools", "command-center"],
    source: "מסכי סוכני AI",
  },
  {
    id: "approvals",
    title: "אישורים ואישור אנושי",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["אישור", "אישורים", "לאשר", "מאשר", "מי מאשר", "לדחות", "approval", "approve", "human approval", "הרשאה", "הרשאות"],
    summary:
      "פעולה רגישה של סוכן ממתינה לאישור של אדם. בלי אישור היא לא מתבצעת.",
    sections: [
      {
        heading: "מתי נדרש אישור",
        text: "שליחת הודעה ללקוח, חיבור מרחוק ופעולה שמשנה מצב דורשים אישור אנושי.",
      },
      {
        heading: "איך האישור עובד",
        text: "האישור קשור לפעולה המדויקת שהוצגה, חד-פעמי ופג תוקף. שינוי בפעולה אחרי האישור פוסל אותו.",
      },
      {
        heading: "מודל אינו מאשר את עצמו",
        text: "מודל שפה לעולם אינו יכול לאשר פעולה של עצמו.",
      },
    ],
    related: ["tools", "security", "remote-access"],
    source: "מנגנון האישורים",
  },
  {
    id: "tools",
    title: "כלים ורמות סיכון",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["כלי", "כלים", "רמת סיכון", "סיכון", "tool", "tools", "חסום", "ברירת מחדל", "registry"],
    summary:
      "כל כלי שסוכן יכול להפעיל מוגדר מראש עם רמת סיכון ותפקידים מורשים. כלי שלא הוגדר נחסם.",
    sections: [
      {
        heading: "ברירת מחדל: חסום",
        text: "אם כלי לא הוגדר במפורש, אף סוכן אינו יכול להפעיל אותו.",
      },
      {
        heading: "קריאה בלבד מול שינוי",
        text: "כלים לקריאה בלבד נושאים סיכון נמוך. כלים שמשנים מצב או מריצים פעולה דורשים אישור ולעיתים אימות נוסף.",
      },
    ],
    related: ["approvals", "security"],
    source: "רישום הכלים",
  },
  {
    id: "skills",
    title: "סקילים וביצועי סוכנים",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["סקיל", "סקילים", "ביצועים", "דירוג", "איכות", "skill", "skills", "leaderboard", "משוב"],
    summary:
      "סקיל הוא יכולת חוזרת ומוגבלת. סקיל אינו מעניק הרשאה לפעול. כיום קיים דירוג ביצועים לסוכנים ומשוב על איכות.",
    sections: [
      {
        heading: "מה קיים היום",
        text: "מסך הסקילים מציג דירוג ביצועי סוכנים לפי שיעור פתרון, משך ועלות. מסך איכות AI מציג משוב על תשובות.",
      },
      {
        heading: "מה עדיין לא קיים",
        text: "מאגר סקילים מאושרים וניתוב סקיל למשימה מתוכננים ועדיין לא נבנו.",
      },
    ],
    related: ["agents", "models"],
    source: "מסך סקילים ומפת המערכת",
  },
  {
    id: "models",
    title: "מודלים וספקים",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["מודל", "מודלים", "ספק", "ספקים", "עלות", "model", "models", "provider", "openai", "anthropic", "LLM"],
    summary:
      "מודל הוא רכיב חשיבה ניתן להחלפה. אף ספק אינו חובה, והחלפתו אינה משנה את גבולות האבטחה.",
    sections: [
      {
        heading: "תפקיד המודל",
        text: "מודל מנתח ומציע. הוא אינו מקור אמת ואינו מחליט על הרשאות.",
      },
      {
        heading: "מצב נוכחי",
        text: "קיים ממשק בסיסי לבחירת ספק ומודל. ניתוב מלא לפי עלות, זמינות וסוג משימה טרם נבנה.",
      },
    ],
    related: ["system-map", "skills"],
    source: "מפת המערכת",
  },
  {
    id: "remote-access",
    title: "גישה מרחוק ואבחון נקודות קצה",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["מרחוק", "גישה מרחוק", "חיבור", "teamviewer", "openclaw", "אבחון", "נקודת קצה", "מחשב", "remote"],
    summary:
      "חיבור מרחוק לעמדה של לקוח דורש תמיד אישור אנושי מפורש. אבחון אוטומטי מוגבל לפעולות צרות שהוגדרו מראש.",
    sections: [
      {
        heading: "חיבור מלווה",
        text: "חיבור מרחוק מתבצע רק באישור אדם ובהקשר של קריאה. סוכן אינו מתחבר לבד.",
      },
      {
        heading: "אבחון",
        text: "אבחון נקודות קצה מוגבל לפעולות מוגדרות מראש (למשל בדיקת קיום תוכנה), ללא פקודות חופשיות.",
      },
    ],
    related: ["approvals", "security", "system-map"],
    source: "מפת המערכת",
  },
  {
    id: "security",
    title: "אבטחה וסודות",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["אבטחה", "סוד", "סודות", "סיסמה", "התחברות", "אימות", "2fa", "audit", "ביקורת", "security", "יומן"],
    summary:
      "ההתחברות דורשת אימות דו-שלבי. כל פעולה רגישה נרשמת, וסודות אינם מוצגים בממשק.",
    sections: [
      {
        heading: "זהות",
        text: "ההתחברות מתבצעת דרך Azure AD / Entra עם אימות דו-שלבי.",
      },
      {
        heading: "ביקורת",
        text: "פעולות משתמשים וסוכנים נרשמות ביומן הפעולות, כולל אישורים.",
      },
      {
        heading: "סודות",
        text: "סיסמאות ומפתחות אינם מוצגים במסכים, ביומנים או בתשובות של סוכנים. אין לשתף אותם בהודעות או בקריאות.",
      },
    ],
    related: ["approvals", "tools"],
    source: "מפת המערכת — שכבות אבטחה",
  },
  {
    id: "command-center",
    title: "Command Center",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["command center", "מרכז פיקוד", "פיקוד", "תצוגה", "הדגמה", "חי"],
    summary:
      "תצוגת פיקוד לקריאה בלבד על מערך סוכני ה-AI. חלק מהנתונים בה הם הדגמה ומסומנים ככאלה.",
    sections: [
      {
        heading: "מקור הנתונים",
        text: "כל נתון מסומן לפי מקורו: נתונים שנשלפו מהמערכת, נתונים שמורים, או נתוני הדגמה. נתוני הדגמה אינם משקפים את המערכת.",
      },
      {
        heading: "קריאה בלבד",
        text: "המסך אינו מבצע פעולות ואינו מאשר או דוחה דבר.",
      },
    ],
    related: ["agents", "system-map"],
    source: "מסך Command Center",
  },
  {
    id: "system-map",
    title: "מפת המערכת",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["מפה", "מפת המערכת", "ארכיטקטורה", "טופולוגיה", "טכנולוגיות", "רכיבים", "factory", "פיתוח", "architecture"],
    summary:
      "מפת המערכת מסבירה את מבנה Prodigy ומסמנת מה פעיל היום ומה מתוכנן או בהערכה.",
    sections: [
      {
        heading: "מה במפה",
        text: "שכבות המערכת, גבולות אבטחה ואישור אנושי, זרימת עבודה מקצה לקצה ומלאי טכנולוגיות.",
      },
      {
        heading: "ייצור מול פיתוח",
        text: "סוכני הייצור עובדים על קריאות. מישור הפיתוח (AI Factory) הוא מערכת נפרדת לתיאום כלי פיתוח, ואינו רץ בייצור.",
      },
    ],
    related: ["security", "models"],
    source: "מסך מפת המערכת",
  },
  {
    id: "workflows",
    title: "תהליכי עבודה נפוצים",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["תהליך", "תהליכים", "איך", "עבודה", "תחזוקה", "בדיקות מתוזמנות", "ידע", "workflow", "sharepoint"],
    summary:
      "תיאור קצר של תהליכי עבודה יומיומיים: טיפול בקריאה, תחזוקה מתוכננת ושימוש במאגר הידע.",
    sections: [
      {
        heading: "טיפול בקריאה",
        text: "פתיחת הקריאה, שיוך, ניתוח (ידני או בעזרת סוכן), תיעוד הפתרון וסגירה.",
      },
      {
        heading: "תחזוקה ובדיקות מתוזמנות",
        text: "פריטי תחזוקה ובדיקות מחזוריות נרשמים ומבוצעים לפי לוח זמנים, והתוצאות נשמרות.",
      },
      {
        heading: "מאגר ידע",
        text: "פתרונות שאושרו על ידי אדם נשמרים כמאמרי ידע לשימוש חוזר.",
      },
    ],
    related: ["tickets", "agents"],
    source: "מסכי העבודה השוטפת",
  },
  {
    id: "admin-settings",
    title: "הגדרות ולקוחות (מנהלים)",
    audience: "admin",
    updated: "2026-09-19",
    keywords: ["הגדרות", "חברות", "לקוחות", "טכנאים", "מנהל", "admin", "settings", "פרופיל לקוח"],
    summary:
      "מסכי ניהול חברות, טכנאים והגדרות מערכת זמינים למנהלי מערכת ובעלי מערכת בלבד.",
    sections: [
      {
        heading: "מה כולל ניהול",
        text: "הוספה ועדכון של חברות לקוח, ניהול טכנאים, והגדרות מערכת.",
      },
      {
        heading: "זהירות",
        text: "שינויים במסכים אלה משפיעים על כל המשתמשים. הם נרשמים ביומן הפעולות.",
      },
    ],
    related: ["security"],
    source: "מסכי הניהול",
  },
  {
    id: "help-center",
    title: "מרכז העזרה",
    audience: "all",
    updated: "2026-09-19",
    keywords: ["עזרה", "מרכז עזרה", "help", "שאלה", "שאל", "תמיכה במערכת"],
    summary:
      "מרכז העזרה עונה רק על סמך מסמכי העזרה המאושרים, ומציין מתי אין לו תשובה.",
    sections: [
      {
        heading: "איך זה עובד",
        text: "החיפוש והשאלות נענים ממסמכי עזרה מאושרים וממוספרי גרסה. אם אין מידע רלוונטי, המרכז יאמר זאת במקום לנחש.",
      },
      {
        heading: "מה לא במרכז",
        text: "מרכז העזרה אינו מציג נתוני לקוחות, סודות או פרטים על הרשאות שאינן שלך.",
      },
    ],
    related: ["navigation"],
    source: "מרכז העזרה",
  },
  {
    id: "research-mode",
    title: "מצב מחקר — כשהמערכת לא מנחשת",
    audience: "all",
    updated: "2026-10-12",
    keywords: [
      "מצב מחקר", "מחקר", "ניחוש", "ניחושים", "אי-התאמה", "לא מוסבר", "ידע חסר",
      "תיעוד רשמי", "הסלמה", "research", "research mode", "gate",
    ],
    summary:
      "כשהעובד הדיגיטלי פוגש משהו שאינו מוסבר בתוכנית המאושרת, הוא עוצר ומחפש מקור רשמי במקום לנחש.",
    sections: [
      {
        heading: "מה מפעיל מצב מחקר",
        text: "העובד פוגש שדה, תשובה או מצב שהתוכנית המאושרת אינה מסבירה. במקום להשלים את החסר בהנחה סבירה, הוא עוצר את הפעולה לפני שהיא משנה משהו.",
      },
      {
        heading: "מה קורה אז",
        text: "הפעולה מושהית. העובד מחפש בתיעוד הרשמי של הספק. רק חומר רשמי שמיישב את אי-ההתאמה משחרר את הפעולה להמשך.",
      },
      {
        heading: "כשאין מקור מתאים",
        text: "אם אין חומר רשמי שמסביר את הפער, הפעולה נשארת חסומה ומוסלמת לאדם. אין ניסיון חוזר ואין ניחוש.",
      },
      {
        heading: "מה נדרש ממך",
        text: "אם פעולה שכבר נקבעה מושהית במצב מחקר, ספקו את החומר הרשמי או בחרו דרך פעולה אחרת. הפער עצמו מוצג לך, וניתן להסביר אותו בלי לנחש.",
      },
    ],
    related: ["approvals", "agents"],
    source: "מסך ריצות AI",
  },
  {
    id: "verification",
    title: "אימות לאחר ביצוע",
    audience: "all",
    updated: "2026-10-12",
    keywords: [
      "אימות", "תנאי סיום", "postcondition", "בוצע", "הסתיים", "הצלחה", "ראיה",
      "קריאה חוזרת", "verification", "read back", "לא תאם",
    ],
    summary:
      "פעולה נחשבת מוצלחת רק כשקריאה חוזרת מהמערכת מאשרת שהמצב הרצוי התקבל בפועל.",
    sections: [
      {
        heading: "מה נחשב אימות",
        text: "לאחר הביצוע המערכת קוראת את המצב בחזרה מהמערכת החיצונית ומשווה אותו למצב שהוגדר מראש. ההשוואה הזו היא הראיה — לא הודעת ההצלחה של הכלי.",
      },
      {
        heading: "כשהתוצאה אינה תואמת",
        text: "אם הקריאה החוזרת אינה תואמת למצב הצפוי, הביצוע אינו נחשב מוצלח גם אם הפעולה עצמה הסתיימה. הפער מוצג לבדיקה.",
      },
      {
        heading: "מה זה אומר בפועל",
        text: "סטטוס שמראה שהפעולה הסתיימה אינו מספיק כדי לדווח שהמשימה הושלמה. בדקו את ראית האימות לפני שמעדכנים לקוח.",
      },
    ],
    related: ["approvals", "workflows"],
    source: "מסך עבודה וראיות",
  },
];

export function topicsForRole(role) {
  const isAdmin = role === "admin" || role === "owner";
  return HELP_TOPICS.filter((t) => t.audience === "all" || (t.audience === "admin" && isAdmin));
}

export function findTopic(id, role) {
  return topicsForRole(role).find((t) => t.id === id) || null;
}

// ── grounded retrieval (no generation) ───────────────────────────────────────

const STOPWORDS = new Set([
  "מה", "איך", "מי", "מתי", "למה", "האם", "זה", "את", "של", "על", "עם", "אני", "אפשר", "יש", "לא", "כן",
  "או", "גם", "אם", "כל", "the", "a", "an", "is", "are", "how", "what", "do", "does", "to", "of", "in", "i",
]);

const HEBREW_PREFIXES = "והבלמשכ";

function normalize(text) {
  return String(text)
    .toLowerCase()
    .replace(/[֑-ׇ]/g, "")
    .replace(/[^\p{L}\p{N}\s-]/gu, " ");
}

function tokenize(text) {
  const out = new Set();
  for (const raw of normalize(text).split(/\s+/)) {
    if (raw.length < 2 || STOPWORDS.has(raw)) continue;
    out.add(raw);
    // Strip up to two stacked Hebrew prefix letters (e.g. ו+ה in "והקריאות").
    let stem = raw;
    for (let i = 0; i < 2 && stem.length > 3 && HEBREW_PREFIXES.includes(stem[0]); i += 1) {
      stem = stem.slice(1);
      out.add(stem);
    }
  }
  return out;
}

// Inverse-document-frequency weight: a token found in many topics (e.g. "AI",
// "סוכן") says little about which topic is meant, so it counts for less.
const DOC_FREQ = (() => {
  const df = new Map();
  for (const t of HELP_TOPICS) {
    const all = tokenize(
      [t.title, t.keywords.join(" "), t.summary, ...t.sections.map((x) => `${x.heading} ${x.text}`)].join(" ")
    );
    for (const tok of all) df.set(tok, (df.get(tok) || 0) + 1);
  }
  return df;
})();

function weight(token) {
  return Math.log(1 + HELP_TOPICS.length / (DOC_FREQ.get(token) || 1));
}

function hits(haystackTokens, queryTokens) {
  let n = 0;
  for (const q of queryTokens) if (haystackTokens.has(q)) n += weight(q);
  return n;
}

const MIN_SCORE = 1.5;

function scoreTopic(topic, queryTokens) {
  const titleScore = hits(tokenize(topic.title), queryTokens) * 5;
  const keywordScore = hits(tokenize(topic.keywords.join(" ")), queryTokens) * 4;
  const summaryScore = hits(tokenize(topic.summary), queryTokens) * 2;
  let bestSection = null;
  let bestSectionScore = 0;
  let bodyScore = 0;
  for (const s of topic.sections) {
    const sc = hits(tokenize(`${s.heading} ${s.text}`), queryTokens);
    bodyScore += sc;
    if (sc > bestSectionScore) {
      bestSectionScore = sc;
      bestSection = s;
    }
  }
  return { topic, score: titleScore + keywordScore + summaryScore + bodyScore, section: bestSection };
}

/** Search the topics visible to `role`; returns topics sorted by relevance. */
export function searchTopics(query, role) {
  const q = tokenize(query || "");
  if (q.size === 0) return [];
  return topicsForRole(role)
    .map((t) => scoreTopic(t, q))
    .filter((r) => r.score >= MIN_SCORE)
    .sort((a, b) => b.score - a.score);
}

/**
 * Grounded answer: quotes approved content only. `answered:false` means no
 * approved content matched, and the caller must say so rather than guess.
 */
export function answerQuestion(question, role) {
  const results = searchTopics(question, role).slice(0, 3);
  if (results.length === 0) {
    return { answered: false, version: HELP_CONTENT_VERSION, sources: [] };
  }
  const [best, ...rest] = results;
  return {
    answered: true,
    version: HELP_CONTENT_VERSION,
    summary: best.topic.summary,
    detail: best.section || null,
    sources: [best, ...rest].map((r) => ({
      id: r.topic.id,
      title: r.topic.title,
      source: r.topic.source,
    })),
  };
}
